import React from 'react';

type UseMeasuredFrameSizeArgs = {
  frameId: string;
  getFrameDOMElement: (frameId: string) => HTMLElement | null;
};

/**
 * Returns the width and height of the Frame element in the DOM in world units (before any transformations are applied)
 */
export const useMeasuredFrameSize = ({
  frameId,
  getFrameDOMElement,
}: UseMeasuredFrameSizeArgs) => {
  const measuredSizeRef = React.useRef<{width: number; height: number}>({
    width: 0,
    height: 0,
  });

  const subscribe = React.useCallback(
    (callback: () => void) => {
      let observedElement: HTMLElement | null = null;
      let resizeObserver: ResizeObserver | null = null;
      let retryId: ReturnType<typeof requestAnimationFrame> | null = null;

      function observeElement(element: HTMLElement) {
        resizeObserver?.disconnect();
        observedElement = element;

        resizeObserver = new ResizeObserver((entries) => {
          const entry = entries[0];
          if (!entry) return;

          const {width, height} = entry.contentRect;

          if (!observedElement?.isConnected) {
            resizeObserver?.disconnect();
            retryObserve();
            return;
          }

          measuredSizeRef.current = {width, height};
          callback();
        });

        resizeObserver.observe(element);
      }

      function retryObserve() {
        const element = getFrameDOMElement(frameId);
        if (element) {
          observeElement(element);
        } else {
          retryId = requestAnimationFrame(retryObserve);
        }
      }

      const frameElement = getFrameDOMElement(frameId);
      if (frameElement) {
        observeElement(frameElement);
      } else {
        retryObserve();
      }

      return () => {
        if (retryId != null) cancelAnimationFrame(retryId);
        resizeObserver?.disconnect();
      };
    },
    [frameId, getFrameDOMElement]
  );

  const getSnapshot = React.useCallback(() => {
    return measuredSizeRef.current;
  }, []);

  return React.useSyncExternalStore(subscribe, getSnapshot);
};
